import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Image as KonvaImage, Group, Rect } from 'react-konva';
import { observer } from 'mobx-react-lite';
import { MASK_SHAPES } from '../toolbar/image-mask-panel';

// Custom Konva filter: Sepia
const SepiaFilter = function (imageData) {
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    data[i]     = Math.min(255, (r * 0.393) + (g * 0.769) + (b * 0.189));
    data[i + 1] = Math.min(255, (r * 0.349) + (g * 0.686) + (b * 0.168));
    data[i + 2] = Math.min(255, (r * 0.272) + (g * 0.534) + (b * 0.131));
  }
};

// Custom Konva filter: Warm (boost red, reduce blue)
const WarmFilter = function (imageData) {
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    data[i]     = Math.min(255, data[i] + 25);      // red up
    data[i + 1] = Math.min(255, data[i + 1] + 10);  // green slightly up
    data[i + 2] = Math.max(0, data[i + 2] - 20);    // blue down
  }
};

// Custom Konva filter: Cold (boost blue, reduce red)
const ColdFilter = function (imageData) {
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    data[i]     = Math.max(0, data[i] - 20);         // red down
    data[i + 1] = Math.min(255, data[i + 1] + 5);    // green slightly up
    data[i + 2] = Math.min(255, data[i + 2] + 25);   // blue up
  }
};

const ImageElementComponent = observer(({ element, isSelected, onSelect, onTransformEnd, onDragEnd, groupChild }) => {
  const imageRef = useRef(null);
  const groupRef = useRef(null);
  const [image, setImage] = useState(null);

  // Persistent auto-crop state: tracks the visible portion of the source image.
  // Initialized on image load (cover-fit centered), then updated directionally on resize.
  const autoCropRef = useRef(null);
  const autoCropSrcRef = useRef(null); // tracks which image the crop was computed for

  useEffect(() => {
    if (!element.src) {
      setImage(null);
      return;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
    };
    img.onerror = () => {
      console.warn('Failed to load image:', element.src.substring(0, 100));
    };
    img.src = element.src;
  }, [element.src]);

  // Build Konva filters array
  const filters = [];
  if (element.blurEnabled) filters.push(window.Konva.Filters.Blur);
  if (element.brightnessEnabled) filters.push(window.Konva.Filters.Brighten);
  if (element.grayscaleEnabled) filters.push(window.Konva.Filters.Grayscale);
  if (element.sepiaEnabled) filters.push(SepiaFilter);
  if (element.warmEnabled) filters.push(WarmFilter);
  if (element.coldEnabled) filters.push(ColdFilter);

  const hasFilters = filters.length > 0;
  const hasBorder = element.borderSize > 0 && element.borderColor;
  const needsCache = hasFilters || element.cornerRadius > 0 || element.shadowEnabled;

  // Cache the image node whenever filters, cornerRadius, or shadow change so they take effect
  useEffect(() => {
    const node = imageRef.current;
    if (!node || !image) return;
    if (needsCache) {
      try {
        node.cache();
      } catch (e) { /* ignore cache errors */ }
    } else {
      try {
        node.clearCache();
      } catch (e) { /* ignore */ }
    }
  }, [image, needsCache, element.blurEnabled, element.blurRadius, element.brightnessEnabled, element.brightness, element.grayscaleEnabled, element.sepiaEnabled, element.warmEnabled, element.coldEnabled, element.cornerRadius, element.shadowEnabled, element.shadowBlur, element.shadowOffsetX, element.shadowOffsetY, element.shadowColor]);

  if (!element.visible) return null;

  const handleDragEnd = (e) => {
    const target = groupRef.current || e.target;
    element.set({
      x: target.x(),
      y: target.y(),
    });
    if (onDragEnd) onDragEnd(e);
  };

  // Helper: update autoCrop directionally based on which edges moved.
  // Stores IDEAL (unclamped) values so resizing always reverses correctly.
  // Clamping to image bounds happens at render time only.
  const updateAutoCrop = (oldX, oldY, oldW, oldH, newX, newY, newW, newH) => {
    const crop = autoCropRef.current;
    if (!crop || !image) return;

    const newAspect = newW / newH;

    const xMoved = Math.abs(newX - oldX) > 0.5;
    const yMoved = Math.abs(newY - oldY) > 0.5;
    const wChanged = Math.abs(newW - oldW) > 0.5;
    const hChanged = Math.abs(newH - oldH) > 0.5;

    let cx = crop.x, cy = crop.y, cw = crop.w, ch = crop.h;

    if (wChanged && !hChanged) {
      // Width only — adjust crop width, keep crop height fixed
      cw = ch * newAspect;
      if (xMoved) cx = crop.x + crop.w - cw; // left edge dragged → anchor right
    } else if (hChanged && !wChanged) {
      // Height only — adjust crop height, keep crop width fixed
      ch = cw / newAspect;
      if (yMoved) cy = crop.y + crop.h - ch; // top edge dragged → anchor bottom
    } else if (wChanged && hChanged) {
      // Corner resize — keep ch as base, derive cw
      cw = ch * newAspect;
      if (xMoved) cx = crop.x + crop.w - cw;
      if (yMoved) cy = crop.y + crop.h - ch;
    }

    // Store ideal values — NO clamping here
    autoCropRef.current = { x: cx, y: cy, w: cw, h: ch };
  };

  // Live transform — reset scale, set node dimensions, update crop directionally
  const handleTransform = (e) => {
    const node = groupRef.current || e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    const oldX = element.x, oldY = element.y;
    const oldW = element.width, oldH = element.height;
    const newW = Math.max(5, node.width() * Math.abs(scaleX));
    const newH = Math.max(5, node.height() * Math.abs(scaleY));
    const newX = node.x();
    const newY = node.y();

    node.scaleX(1);
    node.scaleY(1);
    node.width(newW);
    node.height(newH);

    updateAutoCrop(oldX, oldY, oldW, oldH, newX, newY, newW, newH);

    element.set({ x: newX, y: newY, width: newW, height: newH, rotation: node.rotation() });
  };

  const handleTransformEnd = (e) => {
    const node = groupRef.current || e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    const oldX = element.x, oldY = element.y;
    const oldW = element.width, oldH = element.height;
    const newW = Math.max(5, node.width() * Math.abs(scaleX));
    const newH = Math.max(5, node.height() * Math.abs(scaleY));
    const newX = node.x();
    const newY = node.y();

    node.scaleX(1);
    node.scaleY(1);
    node.width(newW);
    node.height(newH);

    updateAutoCrop(oldX, oldY, oldW, oldH, newX, newY, newW, newH);

    element.set({ x: newX, y: newY, width: newW, height: newH, rotation: node.rotation() });
    if (onTransformEnd) onTransformEnd(e);
  };

  // Crop calculations
  const modelHasCrop = image && (
    element.cropX !== 0 ||
    element.cropY !== 0 ||
    element.cropWidth !== 1 ||
    element.cropHeight !== 1
  );

  // Initialize auto-crop when image changes (cover-fit, centered)
  if (image && image !== autoCropSrcRef.current) {
    autoCropSrcRef.current = image;
    if (!modelHasCrop) {
      const natW = image.naturalWidth || image.width;
      const natH = image.naturalHeight || image.height;
      if (natW > 0 && natH > 0) {
        const elemAspect = element.width / element.height;
        const imgAspect = natW / natH;
        if (imgAspect > elemAspect) {
          const ch = natH;
          const cw = natH * elemAspect;
          autoCropRef.current = { x: (natW - cw) / 2, y: 0, w: cw, h: ch };
        } else {
          const cw = natW;
          const ch = natW / elemAspect;
          autoCropRef.current = { x: 0, y: (natH - ch) / 2, w: cw, h: ch };
        }
      }
    } else {
      autoCropRef.current = null;
    }
  }
  if (!image) { autoCropSrcRef.current = null; autoCropRef.current = null; }

  let cropProps = {};
  if (modelHasCrop) {
    cropProps = {
      crop: {
        x: element.cropX * image.naturalWidth,
        y: element.cropY * image.naturalHeight,
        width: element.cropWidth * image.naturalWidth,
        height: element.cropHeight * image.naturalHeight,
      },
    };
  } else if (autoCropRef.current && image) {
    // Render-time clamping: fit ideal crop within source image bounds
    // while maintaining element aspect ratio.
    const ac = autoCropRef.current;
    const natW = image.naturalWidth || image.width;
    const natH = image.naturalHeight || image.height;
    const elemAspect = element.width / element.height;

    let rw = ac.w, rh = ac.h, rx = ac.x, ry = ac.y;

    // Clamp dimensions to image bounds, maintaining aspect ratio
    if (rw > natW) { rw = natW; rh = natW / elemAspect; }
    if (rh > natH) { rh = natH; rw = natH * elemAspect; }
    rw = Math.max(1, rw);
    rh = Math.max(1, rh);

    // Clamp position
    rx = Math.max(0, Math.min(rx, natW - rw));
    ry = Math.max(0, Math.min(ry, natH - rh));

    cropProps = { crop: { x: rx, y: ry, width: rw, height: rh } };
  }

  // Mask clipping (clipFunc only works on Container nodes like Group)
  const maskShape = element.clipSrc
    ? MASK_SHAPES.find((s) => s.clipId === element.clipSrc)
    : null;

  const hasMask = !!maskShape;

  const clipFunc = hasMask
    ? (ctx) => { maskShape.clipFunc(ctx, element.width, element.height); }
    : undefined;

  const imageNode = (
    <KonvaImage
      ref={imageRef}
      image={image}
      x={0}
      y={0}
      width={element.width}
      height={element.height}
      scaleX={element.flipX ? -1 : 1}
      scaleY={element.flipY ? -1 : 1}
      offsetX={element.flipX ? element.width : 0}
      offsetY={element.flipY ? element.height : 0}
      cornerRadius={element.cornerRadius}
      filters={hasFilters ? filters : undefined}
      blurRadius={element.blurEnabled ? element.blurRadius : undefined}
      brightness={element.brightnessEnabled ? element.brightness : undefined}
      shadowEnabled={element.shadowEnabled}
      shadowColor={element.shadowColor}
      shadowBlur={element.shadowBlur}
      shadowOffsetX={element.shadowOffsetX}
      shadowOffsetY={element.shadowOffsetY}
      shadowOpacity={element.shadowOpacity}
      {...cropProps}
    />
  );

  return (
    <Group
      ref={groupRef}
      id={element.id}
      name={element.name || 'image'}
      x={element.x}
      y={element.y}
      width={element.width}
      height={element.height}
      rotation={element.rotation}
      opacity={element.opacity}
      draggable={!groupChild && element.draggable}
      visible={element.visible}
      clipFunc={clipFunc}
      onClick={(e) => { if (!groupChild && onSelect) onSelect(e); }}
      onTap={(e) => { if (!groupChild && onSelect) onSelect(e); }}
      onDragEnd={handleDragEnd}
      onTransform={handleTransform}
      onTransformEnd={handleTransformEnd}
    >
      {imageNode}
      {hasBorder && (
        <Rect
          x={0}
          y={0}
          width={element.width}
          height={element.height}
          stroke={element.borderColor}
          strokeWidth={element.borderSize}
          cornerRadius={element.cornerRadius}
          listening={false}
        />
      )}
    </Group>
  );
});

ImageElementComponent.displayName = 'ImageElement';
export default ImageElementComponent;
